import { useState } from 'react';
import { Box, Typography, Button, Avatar, Paper, Rating, Link, Popover, IconButton, Tooltip, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';

const PageSummary = ({ title, rating, ownerAvatar, ownerName, ownerId, isFavorite, onToggleFavorite, onQuote, isOwner }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.href;
  const shareText = `Mira esta página de servicios: ${title}`;

  const handleShare = async (e) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
      } catch (_) {}
    } else {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socialLinks = [
    {
      label: 'WhatsApp',
      icon: <WhatsAppIcon sx={{ color: 'marcas.whatsapp' }} />,
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    },
    {
      label: 'Facebook',
      icon: <FacebookIcon sx={{ color: 'marcas.facebook' }} />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      label: 'X (Twitter)',
      icon: <XIcon sx={{ color: 'common.black' }} />,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    }
  ];

  // Mismo ancho que el banner de arriba y que el contenido de abajo. A la izquierda va quién es la página
  // (nombre, calificación y dueño) y a la derecha, pegadas al borde, las acciones; en móvil se apilan.
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, md: 3 },
        position: 'relative',
        zIndex: 2,
        backgroundColor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', md: '2rem' },
              minWidth: 0,
              wordBreak: 'break-word'
            }}
          >
            {title}
          </Typography>

          {Number(rating) > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50', p: '4px 12px', borderRadius: '20px' }}>
              <Rating value={Number(rating)} precision={0.1} readOnly size="small" sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {Number(rating).toFixed(1)}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'grey.50', p: '4px 12px', borderRadius: '20px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Nuevo
              </Typography>
            </Box>
          )}
        </Box>

        <Link
          component={RouterLink}
          to={`/perfil/${ownerId}`}
          underline="none"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 1.5,
            maxWidth: '100%',
            cursor: ownerId ? 'pointer' : 'default',
            '&:hover': { opacity: ownerId ? 0.8 : 1 }
          }}
        >
          <Avatar
            src={ownerAvatar}
            sx={{ width: 40, height: 40, border: 2, borderColor: 'divider', flexShrink: 0 }}
          />
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 0, wordBreak: 'break-word' }}>
            {ownerName}
          </Typography>
        </Link>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
        <Button
          variant="text"
          color="primary"
          onClick={onToggleFavorite}
          aria-label={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
          sx={{ minWidth: 'auto', p: 1, '&:hover': { bgcolor: 'sutil' } }}
        >
          {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </Button>

        {/* En pantallas muy angostas el botón de compartir es solo el ícono */}
        <IconButton
          aria-label="Compartir página"
          onClick={handleShare}
          sx={{
            display: { xs: 'inline-flex', sm: 'none' },
            color: 'primary.main',
            border: 1, borderColor: 'divider',
            borderRadius: '8px'
          }}
        >
          <IosShareIcon />
        </IconButton>

        <Button
          variant="contained"
          startIcon={<IosShareIcon />}
          onClick={handleShare}
          sx={{
            textTransform: 'none',
            bgcolor: 'primary.main',
            borderRadius: '8px',
            px: 3,
            whiteSpace: 'nowrap',
            flex: { sm: 1, md: 'none' },
            display: { xs: 'none', sm: 'inline-flex' }
          }}
        >
          Compartir página
        </Button>

        <Button
          variant="outlined"
          onClick={onQuote}
          disabled={isOwner}
          sx={{
            textTransform: 'none',
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '8px',
            px: 3,
            flex: { xs: 1, md: 'none' },
            '&.Mui-disabled': { borderColor: 'divider', color: 'text.secondary' }
          }}
        >
          Cotizar
        </Button>
      </Box>

      {/* Popover fallback para navegadores sin Web Share API */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: { borderRadius: '12px', p: 2, width: 280, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
          Compartir
        </Typography>

        {/* Copiar enlace */}
        <Box
          onClick={handleCopy}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: '8px 12px', borderRadius: '8px', cursor: 'pointer',
            border: 1, borderColor: 'divider', mb: 2,
            '&:hover': { bgcolor: 'grey.50' }
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" display="block">Enlace</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{shareUrl}</Typography>
          </Box>
          {copied
            ? <CheckIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
            : <ContentCopyIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
          }
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Redes sociales */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          {socialLinks.map(({ label, icon, url }) => (
            <Tooltip key={label} title={label} placement="top">
              <IconButton
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  border: 1, borderColor: 'divider', borderRadius: '8px',
                  p: 1.5, '&:hover': { bgcolor: 'grey.50' }
                }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Paper>
  );
};

export default PageSummary;
